/**
 * A channel's Chat Plane: paginated history (newest-first, CORD-03 §3) plus a
 * live subscription, folded into a render-ready timeline.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { useNostr } from "@nostrify/react";
import type { NostrEvent } from "@nostrify/nostrify";

import { channelKey } from "@/lib/concord/crypto";
import { decodeStreamEvent } from "@/lib/concord/stream";
import { foldChatEvents } from "@/lib/concord/chat";
import type { ChannelState } from "@/lib/concord/editions";
import { communityRelays } from "@/hooks/useChama";
import type { DecodedStreamEvent, Membership, StreamKey } from "@/lib/concord/types";

interface MessagesPage {
  decoded: DecodedStreamEvent[];
  /** Oldest wrap second seen in this page, for `until` pagination. */
  oldestSec: number | null;
  /** Raw wraps returned by the relays (before decoding). */
  fetched: number;
}

const PAGE_SIZE = 50;

export function useChannelMessages(
  membership: Membership | undefined,
  channel: ChannelState | undefined,
  opts: { myPub?: string; banlist: Set<string> },
) {
  const { nostr } = useNostr();
  const queryClient = useQueryClient();
  const communityId = membership?.communityId;
  const channelId = channel?.id;
  const epoch = membership?.current.root_epoch ?? 0;
  const relays = communityRelays(membership);
  const relaysKey = relays.join(",");
  const streamRef = useRef<StreamKey | null>(null);

  const queryKey = ["chama", "messages", communityId, channelId];

  const query = useInfiniteQuery({
    queryKey,
    enabled: Boolean(membership && channel && !channel.private),
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const m = membership!.current;
      const stream = await channelKey(m.community_root, channelId!, m.root_epoch);
      streamRef.current = stream;
      const filter: {
        kinds: number[];
        authors: string[];
        limit: number;
        until?: number;
      } = { kinds: [1059], authors: [stream.pk], limit: PAGE_SIZE };
      if (pageParam) filter.until = pageParam;
      const events = await nostr.group(relays).query([filter], { signal });
      const decoded = events
        .map((e) => decodeStreamEvent(e, stream))
        .filter((d): d is DecodedStreamEvent => d !== null);
      const oldestSec = events.length
        ? Math.min(...events.map((e) => e.created_at))
        : null;
      return { decoded, oldestSec, fetched: events.length } satisfies MessagesPage;
    },
    getNextPageParam: (last) =>
      last.oldestSec !== null && last.fetched >= PAGE_SIZE
        ? last.oldestSec - 1
        : undefined,
    staleTime: 15_000,
  });

  /** Append a freshly published wrap (ours or a live peer's) to the cache. */
  const appendWrap = useCallback(
    async (wrap: NostrEvent) => {
      if (!streamRef.current) {
        const m = membership?.current;
        if (!m || !channelId) return;
        streamRef.current = await channelKey(m.community_root, channelId, m.root_epoch);
      }
      const decoded = decodeStreamEvent(wrap, streamRef.current);
      if (!decoded) return;
      queryClient.setQueryData<InfiniteData<MessagesPage>>(queryKey, (data) => {
        if (!data || data.pages.length === 0) {
          return {
            pages: [{ decoded: [decoded], oldestSec: wrap.created_at, fetched: 1 }],
            pageParams: [undefined],
          };
        }
        const exists = data.pages.some((p) =>
          p.decoded.some((d) => d.wrap.id === wrap.id),
        );
        if (exists) return data;
        const [first, ...rest] = data.pages;
        return {
          ...data,
          pages: [{ ...first, decoded: [...first.decoded, decoded] }, ...rest],
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, communityId, channelId],
  );

  // Live subscription: stream new wraps into the cache as they arrive.
  useEffect(() => {
    if (!membership || !channelId || channel?.private) return;
    const m = membership.current;
    const controller = new AbortController();
    (async () => {
      try {
        const stream = await channelKey(m.community_root, channelId, m.root_epoch);
        streamRef.current = stream;
        const since = Math.floor(Date.now() / 1000) - 5;
        const sub = nostr
          .group(relays)
          .req([{ kinds: [1059], authors: [stream.pk], since }], {
            signal: controller.signal,
          });
        for await (const msg of sub) {
          if (msg[0] === "EVENT") {
            void appendWrap(msg[2] as NostrEvent);
          }
        }
      } catch {
        // closed on unmount or relay drop
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId, channelId, relaysKey]);

  const messages = useMemo(() => {
    const all = (query.data?.pages ?? []).flatMap((p) => p.decoded);
    const seen = new Set<string>();
    const unique = all.filter((d) => {
      if (seen.has(d.rumor.id)) return false;
      seen.add(d.rumor.id);
      return true;
    });
    return foldChatEvents(unique, {
      channelId: channelId ?? "",
      epoch,
      myPub: opts.myPub,
      banlist: opts.banlist,
    }).messages;
  }, [query.data, channelId, epoch, opts.myPub, opts.banlist]);

  return {
    messages,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    appendWrap,
  };
}
